import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Clock, DatabaseZap, RefreshCw, ShieldCheck, TriangleAlert } from 'lucide-react';
import {
  CLE_QUERY_STATUT,
  chargerStatutBacASable,
  formaterDonneesAu,
  lancerRefreshBacASable,
  messageRefusRefresh,
  normaliserPct,
  type RaisonRefusRefresh,
} from '../../lib/bac-a-sable';

/**
 * Contrôle « refresh bac à sable » du header.
 *
 * Visible UNIQUEMENT si (a) l'utilisateur est admin et (b) l'API déclare
 * `sandbox: true`. Partout ailleurs — production, non-admin, API pas encore
 * déployée (404), serveur injoignable — le composant ne rend RIEN : mieux vaut
 * un header sans bouton qu'un bouton qui ment sur l'état de la base.
 *
 * Au repos il tient en une pastille discrète (« Bac à sable »). Pendant
 * l'import il devient un indicateur avec une jauge fine et le pourcentage ;
 * l'étape en cours vit dans l'infobulle, pas à l'écran — le header n'est pas
 * une console de log.
 *
 * ⚠️ 14/08 — ce bouton disait « Données au JJ/MM HH:MM » au repos (date de la
 * copie de production). Depuis que le header porte la fraîcheur RÉELLE des
 * données pour tout le monde (`FraicheurDonnees`, alimentée par le moteur de
 * mise à jour), cette phrase-là ne lui appartient plus : deux « Données au »
 * voisins, sur deux horloges différentes, se contredisaient à l'œil. Ce
 * contrôle garde donc son GESTE, et la date de copie reste lisible au survol
 * comme dans le dialogue de confirmation — rien n'est perdu, seule la place
 * change.
 */
const POLL_EN_COURS_MS = 5_000;
const POLL_AU_REPOS_MS = 60_000;
const DUREE_FLASH_SUCCES_MS = 8_000;
/** Garde-fou : si l'API ne confirme jamais le démarrage, on ne reste pas bloqué en « en cours ». */
const GARDE_LANCEMENT_MS = 30_000;

export function BacASableControl({ estAdmin }: { readonly estAdmin: boolean }): JSX.Element | null {
  const queryClient = useQueryClient();
  const [dialogueOuvert, setDialogueOuvert] = useState(false);
  const [flashSucces, setFlashSucces] = useState(false);
  const [vientDeLancer, setVientDeLancer] = useState(false);
  const runningPrecedent = useRef<boolean | null>(null);
  const boutonRef = useRef<HTMLButtonElement>(null);

  const { data: statut = null } = useQuery({
    queryKey: CLE_QUERY_STATUT,
    queryFn: ({ signal }) => chargerStatutBacASable(signal),
    enabled: estAdmin,
    // Un échec ne doit ni réessayer en boucle ni faire clignoter le header :
    // le contrôle disparaît, point.
    retry: false,
    // Cadence lue sur la donnée fraîche de la query elle-même : 5 s tant que
    // l'import tourne, une minute au repos (assez pour voir « Données au »
    // bouger si le refresh a été lancé ailleurs).
    refetchInterval: (q) => (q.state.data?.running ? POLL_EN_COURS_MS : POLL_AU_REPOS_MS),
    staleTime: 4_000,
  });

  const enCoursApi = statut?.running === true;

  // Fin d'import : on rafraîchit TOUTES les données de l'app d'un coup — les
  // écrans ouverts affichaient jusque-là les chiffres de l'ancienne base.
  useEffect(() => {
    if (!statut) return undefined;
    const avant = runningPrecedent.current;
    runningPrecedent.current = statut.running;
    if (statut.running) setVientDeLancer(false);
    if (avant === true && !statut.running && (statut.exitCode === 0 || statut.exitCode === null)) {
      setFlashSucces(true);
      void queryClient.invalidateQueries();
      const t = setTimeout(() => setFlashSucces(false), DUREE_FLASH_SUCCES_MS);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [statut, queryClient]);

  useEffect(() => {
    if (!vientDeLancer) return undefined;
    const t = setTimeout(() => setVientDeLancer(false), GARDE_LANCEMENT_MS);
    return () => clearTimeout(t);
  }, [vientDeLancer]);

  const surLancement = useCallback(() => {
    setVientDeLancer(true);
    setDialogueOuvert(false);
    void queryClient.invalidateQueries({ queryKey: CLE_QUERY_STATUT });
  }, [queryClient]);

  if (!estAdmin || !statut || !statut.sandbox) return null;

  const enCours = enCoursApi || vientDeLancer;
  const pct = enCoursApi ? normaliserPct(statut.progressionPct) : null;
  const echec = !enCours && statut.exitCode !== null && statut.exitCode !== 0;
  const donneesAu = formaterDonneesAu(statut.donneesAu);

  return (
    <>
      <button
        ref={boutonRef}
        type="button"
        data-testid="bac-a-sable-bouton"
        data-etat={enCours ? 'en-cours' : echec ? 'echec' : flashSucces ? 'succes' : 'repos'}
        disabled={enCours}
        onClick={() => setDialogueOuvert(true)}
        title={titreInfobulle({ enCours, echec, pct, etape: statut.derniereEtape, donneesAu })}
        aria-label={enCours ? `Rafraîchissement du bac à sable en cours${pct !== null ? ` — ${pct} %` : ''}` : 'Rafraîchir le bac à sable'}
        className={[
          'relative h-8 shrink-0 inline-flex items-center gap-1.5 rounded-md border px-2 text-[12px] font-medium overflow-hidden',
          'transition-colors disabled:cursor-default',
          enCours
            ? 'border-[var(--accent)]/30 bg-[var(--accent-soft)] text-[var(--accent-fg)]'
            : echec
              ? 'border-red-200 bg-[var(--danger-soft)] text-[var(--danger-fg)] hover:bg-red-100'
              : flashSucces
                ? 'border-[var(--accent)]/30 bg-[var(--accent-soft)] text-[var(--accent-fg)]'
                : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900',
        ].join(' ')}
      >
        {enCours ? (
          <RefreshCw size={14} className="animate-spin shrink-0" aria-hidden="true" />
        ) : echec ? (
          <TriangleAlert size={14} className="shrink-0" aria-hidden="true" />
        ) : flashSucces ? (
          <CheckCircle2 size={14} className="shrink-0" aria-hidden="true" />
        ) : (
          <DatabaseZap size={14} className="shrink-0" aria-hidden="true" />
        )}

        <span className="hidden lg:inline whitespace-nowrap">
          {enCours
            ? pct !== null
              ? `${pct} %`
              : 'Import en cours'
            : echec
              ? 'Import échoué'
              : flashSucces
                ? 'Bac à sable à jour'
                : 'Bac à sable'}
        </span>

        {enCours && (
          <span
            data-testid="bac-a-sable-jauge"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            {...(pct !== null ? { 'aria-valuenow': pct } : {})}
            aria-label="Progression du rafraîchissement"
            className="absolute inset-x-0 bottom-0 h-[3px] bg-[var(--accent)]/15"
          >
            <span
              className={['block h-full bg-[var(--accent)] transition-[width] duration-500', pct === null ? 'animate-pulse' : ''].join(' ')}
              style={{ width: pct !== null ? `${pct}%` : '100%' }}
            />
          </span>
        )}
      </button>

      {dialogueOuvert && (
        <DialogueRefresh
          donneesAu={donneesAu}
          onFermer={() => {
            setDialogueOuvert(false);
            boutonRef.current?.focus();
          }}
          onLance={surLancement}
        />
      )}
    </>
  );
}

function titreInfobulle(o: {
  readonly enCours: boolean;
  readonly echec: boolean;
  readonly pct: number | null;
  readonly etape: string | null;
  readonly donneesAu: string | null;
}): string {
  if (o.enCours) {
    const tete = o.pct !== null ? `Rafraîchissement en cours — ${o.pct} %` : 'Rafraîchissement en cours';
    return o.etape ? `${tete}\n${o.etape}` : tete;
  }
  if (o.echec) {
    return o.etape
      ? `Le dernier rafraîchissement a échoué.\nDernière étape : ${o.etape}\nCliquez pour réessayer.`
      : 'Le dernier rafraîchissement a échoué. Cliquez pour réessayer.';
  }
  const tete = o.donneesAu
    ? `Bac à sable — données copiées de la production le ${o.donneesAu} (heure de Bruxelles).`
    : 'Bac à sable — date de la dernière copie inconnue.';
  return `${tete}\nCliquez pour rafraîchir.`;
}

/**
 * Dialogue de confirmation — même convention que les modales du monorepo
 * (`modules/herbo-admin/web/components/DesactiverCompteDialog.tsx`) : rôle
 * `alertdialog`, fermeture Escape + clic sur le fond, focus rendu à l'ouvrant.
 */
function DialogueRefresh({
  donneesAu,
  onFermer,
  onLance,
}: {
  readonly donneesAu: string | null;
  readonly onFermer: () => void;
  readonly onLance: () => void;
}): JSX.Element {
  const titreId = useId();
  const [envoi, setEnvoi] = useState(false);
  const [refus, setRefus] = useState<RaisonRefusRefresh | null>(null);
  const annulerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    annulerRef.current?.focus();
    const surTouche = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !envoi) onFermer();
    };
    document.addEventListener('keydown', surTouche);
    return () => document.removeEventListener('keydown', surTouche);
  }, [envoi, onFermer]);

  const confirmer = async (): Promise<void> => {
    setEnvoi(true);
    setRefus(null);
    const res = await lancerRefreshBacASable();
    if (res.ok) {
      onLance();
      return;
    }
    setEnvoi(false);
    setRefus(res.raison);
  };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/55 flex items-center justify-center p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !envoi) onFermer();
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titreId}
        data-testid="bac-a-sable-dialogue"
        className="w-[460px] max-w-[96vw] rounded-xl bg-white shadow-2xl"
      >
        <div className="px-5 pt-5 pb-3">
          <h2 id={titreId} className="text-[15px] font-semibold text-slate-900">
            Rafraîchir le bac à sable ?
          </h2>
          <p className="mt-1 text-[13px] text-slate-500">
            Les données seront rechargées depuis la production
            {donneesAu ? ` (copie actuelle : ${donneesAu})` : ''}.
          </p>
        </div>

        <ul className="px-5 pb-4 space-y-2.5 text-[13px] text-slate-700">
          <LignePoint icone={<Clock size={15} aria-hidden="true" />} ton="neutre">
            Comptez <strong className="font-semibold">15 à 25 minutes</strong>.
          </LignePoint>
          <LignePoint icone={<TriangleAlert size={15} aria-hidden="true" />} ton="alerte">
            Pendant l&apos;import, l&apos;application peut afficher des erreurs — c&apos;est attendu, elles disparaissent à
            la fin.
          </LignePoint>
          <LignePoint icone={<ShieldCheck size={15} aria-hidden="true" />} ton="accent">
            Vos analyses, profils fournisseurs et comptes utilisateurs sont préservés.
          </LignePoint>
        </ul>

        {refus && (
          <p role="alert" className="mx-5 mb-3 rounded-md bg-[var(--danger-soft)] px-3 py-2 text-[12.5px] text-[var(--danger-fg)]">
            {messageRefusRefresh(refus)}
          </p>
        )}

        <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-3">
          <button
            ref={annulerRef}
            type="button"
            onClick={onFermer}
            disabled={envoi}
            className="h-9 rounded-md px-3 text-[13px] font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            type="button"
            data-testid="bac-a-sable-confirmer"
            onClick={() => void confirmer()}
            disabled={envoi}
            className="h-9 inline-flex items-center gap-2 rounded-md px-3.5 text-[13px] font-semibold text-white disabled:opacity-60"
            style={{ background: 'var(--accent)' }}
          >
            {envoi && <RefreshCw size={14} className="animate-spin" aria-hidden="true" />}
            {envoi ? 'Lancement…' : 'Lancer le rafraîchissement'}
          </button>
        </div>
      </div>
    </div>
  );
}

function LignePoint({
  icone,
  ton,
  children,
}: {
  readonly icone: JSX.Element;
  readonly ton: 'neutre' | 'alerte' | 'accent';
  readonly children: React.ReactNode;
}): JSX.Element {
  const couleur = ton === 'alerte' ? 'text-[var(--warn)]' : ton === 'accent' ? 'text-[var(--accent)]' : 'text-slate-400';
  return (
    <li className="flex gap-2.5">
      <span className={`mt-px shrink-0 ${couleur}`}>{icone}</span>
      <span>{children}</span>
    </li>
  );
}
